<template>
  <view-container>
    <browse-list
      :game="game"
      :items="guides"
      :loading="isLoading"
      :browse-id="browseId"
      :total="guides?.result_total"
      :sort-options="GUIDE_SORT_OPTIONS"
      :tags="tagsData"
      :card="GUIDE_CARD"
      @fetch:paginate-next="fetchPaginateNext"
      @fetch:query="fetchGuides"
    >
    </browse-list>
  </view-container>
</template>

<script>
import { getGuidesForGameRequest, getGuideTagsRequest } from '@services'
import { usePermissions, useAsync, useRoute } from '@composables'
import { USER_GUIDE_BROWSE_ROUTE } from '@config/routeNames.js'
import ViewContainer from '@components/View/ViewContainer.vue'
import { mergeApiResultData } from '@helpers/requestAll.js'
import { GUIDE_FILTERS_TO_HIDE } from '@config/guides.js'
import BrowseList from '@components/List/BrowseList.vue'
import { GUIDE_SORT_OPTIONS } from '@config/sort.js'
import { KEYS_GUIDES } from '@config/storeKeys.js'
import { filterSettings } from '@config/game.js'
import { computed, provide, watch } from 'vue'
import { GUIDE_CARD } from '@config/card.js'
import { store } from '@stores'
import useFilter, {
  normalizeGuideFilters,
  getDateFilter,
  getAPIFilter,
} from '@composables/useFilter.js'
import {
  userStatsStore,
  guideStore,
  gameStore,
  authStore,
  userStore,
} from '@stores'

const MAX_TAGS_TO_SHOW = 20

export default {
  components: {
    ViewContainer,
    BrowseList,
  },
  setup() {
    const { addGuides, getGuides } = guideStore()
    const { getPublicGame } = gameStore()

    const { routeQuery, getUserNameId, routeName } = useRoute()
    const { isGameModerator } = usePermissions()
    const { addUserStats } = userStatsStore()
    const { isLoggedIn, user: myUser } = authStore()
    const { generateId } = store()
    const userNameId = getUserNameId().value
    const user = userStore().getUser(userNameId)
    const game = getPublicGame()

    const browseId = generateId(
      KEYS_GUIDES,
      `${game.value.name_id}${userNameId || ''}`
    )
    const { setFilter, settingsTags, resetFilter, toggleFilterMenu } =
      useFilter(browseId)

    provide('settingsTags', settingsTags)

    const guides = getGuides(`${game.value.name_id}${userNameId || ''}`)

    const isLoading = computed(() => loadingGuides.value || loadingTags.value)
    const gameImage = computed(() => game.value?.header.original)

    const isMe = computed(
      () => isLoggedIn.value && myUser.value?.name_id === userNameId
    )

    const showAdminFilters = computed(() => isGameModerator.value || isMe.value)

    const {
      data: guidesData,
      loading: loadingGuides,
      run: runFetchGuides,
      error: guidesErrors,
    } = useAsync((query) => getGuidesForGameRequest(query))

    const {
      data: tagsData,
      loading: loadingTags,
      error: tagsErrors,
      run: runFetchTags,
    } = useAsync(() => getGuideTagsRequest())

    watch(
      [game, showAdminFilters],
      async ([_game, _showAdminFilters]) => {
        if (_game) {
          // Get route query before watch triggering filter reset in useBrowse.
          let queryTags = routeQuery.value.tags
          const tagsNotIn = routeQuery.value['tags-not-in'] || ''
          const dateTags = routeQuery.value.date_live || ''

          await runFetchTags()

          if (!tagsErrors.value) {
            const allTags = tagsData.value?.data
              ?.filter((tag) => !GUIDE_FILTERS_TO_HIDE.includes(tag.name))
              ?.sort((a, b) => a.name.localeCompare(b.name))

            const tags = allTags
              .toSorted((a, b) => b.count - a.count)
              .slice(0, MAX_TAGS_TO_SHOW)
              .toSorted((a, b) => a.name.localeCompare(b.name))
              .map((tag) => tag.name)

            queryTags = [filterSettings.value.tags, queryTags]
              .filter((tag) => tag)
              .join(',')

            const adminFilters = [routeQuery.value.admin]
              .filter((filter) => filter)
              .join(',')

            // setting filter triggers fetch from watch in BrowseList
            setFilter(
              normalizeGuideFilters(
                tags,
                {
                  tags: queryTags,
                  'tags-not-in': tagsNotIn,
                  date_live: dateTags,
                  admin: adminFilters,
                },
                _showAdminFilters,
                allTags.map((tag) => tag.name),
                false
              ),
              !queryTags && !dateTags
            )
          } else {
            setFilter([])
          }
        }
      },
      {
        immediate: true,
      }
    )

    watch(() => filterSettings.value.tags, resetFilter)

    async function fetchGuides(query) {
      const _query = { ...query }
      delete _query.platforms
      if (userNameId) {
        _query.submitted_by = user.value?.id
      }

      getAPIFilter(_query)
      getDateFilter(_query)
      await runFetchGuides(_query)

      if (!guidesErrors.value) {
        _updateStore(guidesData.value, query)

        if (routeName.value === USER_GUIDE_BROWSE_ROUTE) {
          const total = guidesData.value.result_total
          addUserStats(userNameId, { guide_count: total })
        }
      }
    }

    async function fetchPaginateNext(query) {
      const _query = { ...query }
      delete _query.platforms

      getAPIFilter(_query)
      getDateFilter(_query)
      await runFetchGuides(_query)

      if (!guidesErrors.value) {
        _updateStore(mergeApiResultData(guides.value, guidesData.value), query)
      }
    }

    function _updateStore(data, query) {
      addGuides(`${game.value.name_id}${userNameId || ''}`, data, query)
    }

    return {
      GUIDE_SORT_OPTIONS,
      fetchPaginateNext,
      toggleFilterMenu,
      fetchGuides,
      GUIDE_CARD,
      gameImage,
      isLoading,
      browseId,
      tagsData,
      guides,
      game,
    }
  },
}
</script>
